import React from 'react';
import { motion } from 'motion/react';
import { Shield, Zap, BarChart3, Cpu, CheckCircle2, ArrowRight, Lock, Eye, Activity, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/src/components/SEO';
import { organizationSchema } from '@/src/lib/schema';

export default function Complytics() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="Complytics – AI-Powered Compliance Platform by AGRANDINTL"
        description="Complytics is an AI-driven security monitoring platform that automates compliance for PCI, SOC2, and GDPR, designed for businesses scaling in the cloud."
        keywords="ai compliance platform, automated pci compliance, soc2 automation, security monitoring software, complytics, AGRANDINTL"
        schema={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Complytics",
          "operatingSystem": "Web-based",
          "applicationCategory": "BusinessApplication, SecurityApplication",
          "author": organizationSchema,
          "description": "AI-powered compliance and security monitoring platform for modern businesses."
        }}
      />
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex flex-col justify-center overflow-hidden pt-32 pb-16">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em] mb-10"
            >
              <Cpu className="w-3.5 h-3.5" /> AI-Powered Compliance
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-10 tracking-tight leading-tight"
            >
              Complytics
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed font-normal max-w-3xl mb-12"
            >
              Monitor your security status in real-time. Complytics helps you stay compliant with industry standards like PCI and SOC2 without the manual paperwork.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-6"
            >
              <Button asChild size="lg" className="h-16 px-10 rounded-xl font-bold text-lg transition-all duration-300">
                <Link to="/contact?product=complytics">Request Platform Demo</Link>
              </Button>
              <div className="flex items-center gap-3 px-5 py-2 rounded-xl bg-white/[0.02] border border-white/5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Platform Active & Monitoring</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-32 border-y border-white/5 bg-secondary/[0.02] relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <div className="space-y-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
                The Platform
              </div>
              <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">
                Continuous <br />
                <span className="text-accent">Security Monitoring</span>
              </h2>
              <p className="text-lg text-muted-foreground font-normal leading-relaxed max-w-2xl">
                Complytics provides a clear view of your security status, mapping your technical settings directly to compliance requirements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {[
                  { icon: Eye, title: "Full Visibility", desc: "See your entire risk surface in a single, high-fidelity pane of glass." },
                  { icon: Activity, title: "Real-time Alerts", desc: "Get notified immediately when a control drifts out of compliance." },
                  { icon: Lock, title: "Automated Evidence", desc: "Collect audit evidence automatically across your entire cloud stack." },
                  { icon: Globe, title: "Global Standards", desc: "Native support for PCI DSS, SOC2, HIPAA, and GDPR frameworks." }
                ].map((item, i) => (
                  <div key={i} className="space-y-3 group">
                    <div className="w-10 h-10 rounded-xl bg-accent/5 border border-white/5 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="text-lg font-bold font-display tracking-tight group-hover:text-accent transition-colors duration-300">{item.title}</h3>
                      <p className="text-sm text-muted-foreground font-normal leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="glass-card rounded-3xl p-6 border-white/5 relative overflow-hidden">
                <div className="aspect-square bg-background/60 rounded-2xl border border-white/10 p-8 flex flex-col justify-between relative">
                  <div className="space-y-8">
                    <div className="flex items-center justify-between">
                      <div className="w-1/2 h-3 bg-accent/10 rounded-full" />
                      <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent">
                        <BarChart3 className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="w-full h-1.5 bg-white/5 rounded-full" />
                      <div className="w-full h-1.5 bg-white/5 rounded-full" />
                      <div className="w-3/4 h-1.5 bg-white/5 rounded-full" />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="h-28 bg-accent/5 rounded-xl border border-accent/10 flex flex-col items-center justify-center gap-1.5 group/stat">
                        <span className="text-3xl font-display font-bold text-accent tracking-tighter">98%</span>
                        <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground group-hover/stat:text-foreground transition-colors">Compliance Score</span>
                      </div>
                      <div className="h-28 bg-white/5 rounded-xl border border-white/5 flex flex-col items-center justify-center gap-1.5 group/stat">
                        <span className="text-3xl font-display font-bold text-foreground tracking-tighter">12</span>
                        <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground group-hover/stat:text-foreground transition-colors">Active Alerts</span>
                      </div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                    <div className="flex gap-2">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="w-8 h-1.5 bg-white/10 rounded-full" />
                      ))}
                    </div>
                    <div className="w-10 h-5 bg-accent/20 rounded-full animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Why <span className="text-accent">Complytics?</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-normal leading-relaxed">
              Traditional compliance is slow, manual, and reactive. Complytics makes it fast, automated, and proactive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Automated Evidence",
                desc: "Stop chasing screenshots. Complytics automatically pulls evidence from your cloud infrastructure, code repos, and HR systems."
              },
              {
                title: "Risk-Based Prioritization",
                desc: "Our AI engine identifies which gaps pose the greatest threat to your business, so you can focus on what matters most."
              },
              {
                title: "Audit Readiness",
                desc: "Be ready for your next audit in days, not months. Generate comprehensive compliance reports with a single click."
              }
            ].map((benefit, i) => (
              <div key={i} className="glass-card p-10 rounded-3xl border-white/5 hover:border-accent/10 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent mb-6 group-hover:bg-accent group-hover:text-background transition-all duration-300">
                  <Zap className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-display font-bold tracking-tight mb-3">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground font-normal leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product of Agrandintl */}
      <section className="py-20 bg-accent/[0.02] border-y border-accent/10">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-4">Agrandintl Ecosystem</p>
          <h3 className="text-2xl font-display font-bold tracking-tight mb-8">Complytics is a flagship product by Agrandintl</h3>
          <Button asChild variant="link" className="text-foreground hover:text-accent transition-colors p-0 h-auto font-bold text-sm uppercase tracking-widest">
            <Link to="/about" className="flex items-center gap-2">
              Learn about Agrandintl <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight mb-10 leading-tight">
              Ready to <br /><span className="text-accent">Simplify?</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground font-normal max-w-3xl mx-auto mb-12 leading-relaxed">
              Join the businesses using Complytics to save time on compliance and build a more transparent security posture.
            </p>
            <Button asChild size="lg" className="h-16 px-12 rounded-xl font-bold text-lg transition-all duration-300">
              <Link to="/contact?product=complytics">Request a Platform Demo</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
